import React, { Dispatch, SetStateAction } from 'react';
import { Button } from 'shared/components';
import { GqlImageBanner, ImageBannerType } from 'types/graphql';
import { Checkmark as CheckmarkIcon } from 'src/svg/checkmark';
import { Hyperlink } from 'src/svg/hyperlink';
import { DiscountTag } from 'src/svg/discount-tag';
import { Menu } from 'src/svg/menu';

import { imageBannerTypes } from './type-step.constants';
import { TypeCards, TypeCard, TypeTitle, TypeDescription, CheckmarkContainer, IconContainer } from './type-step.styles';
import { ButtonContainer } from '../configuration-step/configuration-step.styles';

type TypeStepProps = {
  banner: GqlImageBanner | undefined;
  currentStepIndex: number;
  setStepNumber: (newStep: number) => void;
  setBannerData: (data: Partial<GqlImageBanner>) => void;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  isReadOnly?: boolean;
};

/**
 * Returns the appropriate icon component for the banner type
 */
function getTypeIcon(type: ImageBannerType): JSX.Element {
  switch (type) {
    case ImageBannerType.link:
      return <Hyperlink width='40' height='40' />;
    case ImageBannerType.special:
      return <DiscountTag width='40' height='40' />;
    case ImageBannerType.collection:
      return <Menu width='40' height='40' />;
    case ImageBannerType.category:
      return <Menu width='40' height='40' />;
    default:
      return <Hyperlink width='40' height='40' />;
  }
}

export const TEST_ID_IMAGE_BANNER_TYPE_STEP = 'image-banner-type-step';

/**
 * Type selection step for image banner creation/editing
 * Allows users to select the type of image banner: Link, Special, Collection, or Category
 */
export function TypeStep({
  banner,
  currentStepIndex,
  setStepNumber,
  setBannerData,
  setIsDirty,
  isReadOnly = false,
}: TypeStepProps): JSX.Element {
  const selectedType = banner?.bannerType ?? ImageBannerType.link;

  /**
   * Handles type selection change
   */
  function handleTypeSelect(type: ImageBannerType): void {
    if (isReadOnly) {
      return;
    }

    // When changing type, clear connected entity data if switching away from connected types
    const isConnectedType = type !== ImageBannerType.link;
    const wasConnectedType = selectedType !== ImageBannerType.link;

    if (wasConnectedType && !isConnectedType) {
      // Switching from connected type to link - clear connection data
      setBannerData({
        ...banner,
        bannerType: type,
        connectedId: undefined,
        connectedType: undefined,
      });
    } else if (!wasConnectedType && isConnectedType) {
      // Switching from link to connected type - clear link data (optional, keep for now)
      setBannerData({
        ...banner,
        bannerType: type,
        connectedId: undefined,
        connectedType: undefined,
      });
    } else {
      // Same category of type - just update the type
      setBannerData({
        ...banner,
        bannerType: type,
        connectedId: undefined,
        connectedType: undefined,
      });
    }

    setIsDirty(true);
  }

  /**
   * Handles continue button click
   */
  function handleNext(): void {
    setStepNumber(currentStepIndex + 2);
  }

  return (
    <div data-testid={TEST_ID_IMAGE_BANNER_TYPE_STEP}>
      <TypeCards>
        {imageBannerTypes.map((typeOption) => {
          const isSelected = selectedType === typeOption.key;

          return (
            <TypeCard
              key={typeOption.key}
              $selected={isSelected}
              $disabled={isReadOnly}
              onClick={() => handleTypeSelect(typeOption.key)}
              data-testid={`banner-type-card-${String(typeOption.key)}`}
            >
              <IconContainer>{getTypeIcon(typeOption.key)}</IconContainer>
              <TypeTitle>{typeOption.title}</TypeTitle>
              <TypeDescription>{typeOption.description}</TypeDescription>
              {isSelected && (
                <CheckmarkContainer $disabled={isReadOnly}>
                  <CheckmarkIcon height='7' width='9' viewBox='0 0 9 7' />
                </CheckmarkContainer>
              )}
            </TypeCard>
          );
        })}
      </TypeCards>

      <ButtonContainer>
        <Button onClick={handleNext} disabled={!selectedType} data-testid='type-step-continue-button'>
          Next
        </Button>
      </ButtonContainer>
    </div>
  );
}
